import { useKnockClient } from "@knocklabs/react";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";
import { differenceInCalendarDays } from "date-fns";
import { useEffect } from "react";

import {
  KNOCK_ACCOUNT_AGE_IN_DAYS_ATTRIBUTE,
  KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE,
} from "./constants";

/**
 * Emits the persistent Knock user attributes the dashboard reads to gate the
 * rating prompt:
 *
 * - `accountAgeInDays` — Cognito tenure floor.
 * - `firstMobileAppUseAt` — earliest mobile session timestamp, written
 *   exactly once per user-account. Only set when absent from the server-
 *   side user record (read off the `accountAgeInDays` identify response)
 *   so a later install on a second device cannot overwrite the original.
 *
 * Knock identify is a partial-merge upsert (parent run D-03), so this leaves
 * cooldown timestamps written by `useRequestInAppReview` (and ORB-2176)
 * untouched. Best-effort emit — a failed call is logged and dropped; the next
 * mount (or `workplaceUserId` / `createdAt` change) will retry.
 *
 * Must be called inside `KnockGuidesWrapper` (i.e. with a live
 * `KnockProvider`). The mount component handles that.
 */
export function useEmitReviewPromptAttributes(): void {
  const knockClient = useKnockClient();
  const { profile } = useSession();
  const { createdAt, userId: workplaceUserId } = profile;

  useEffect(() => {
    if (!createdAt) {
      return;
    }

    const createdAtDate = new Date(createdAt);
    if (Number.isNaN(createdAtDate.getTime())) {
      // The session contract says `createdAt` is an ISO string; an unparseable
      // value points at an upstream data-shape bug. Worth a log rather than a
      // silent skip.
      logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, {
        error: new Error("Unparseable createdAt on workplace profile"),
        metadata: { createdAt },
      });
      return;
    }

    // Clamp future-dated `createdAt` to 0 — a clock-skewed signup shouldn't
    // surface as a negative account age.
    const accountAgeInDays = Math.max(0, differenceInCalendarDays(new Date(), createdAtDate));

    void (async () => {
      try {
        // Identify returns the full post-merge user record, so we can read
        // any pre-existing `firstMobileAppUseAt` off this response without a
        // dedicated read call. The second identify below must run after this
        // one resolves — its gate depends on the response.
        const updatedUser = await knockClient.user.identify({
          [KNOCK_ACCOUNT_AGE_IN_DAYS_ATTRIBUTE]: accountAgeInDays,
        });

        // Broad falsy check is deliberate: `User extends GenericData` types
        // arbitrary keys as `any`, so an absent key, `null`, or empty string
        // should all be treated as "not yet set, please write."
        if (Platform.isMobile && !updatedUser[KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE]) {
          await knockClient.user.identify({
            [KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE]: new Date().toISOString(),
          });
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
      }
    })();
  }, [knockClient, workplaceUserId, createdAt]);
}
